// src/Redux/Store.js
import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { combineReducers } from "redux";
import createFilter from "redux-persist-transform-filter";

// --- Import your slices & APIs ---
import { PositionsApi } from "./service/Positions";
import { webSocketSlice } from "./service/Websocket";
import { DevicesApi } from "./service/Devices";
import { UsersApi } from "./service/Users/Users";
import { GeofencesApi } from "./service/Geofences/Geofences";
import { geofencesSlice } from "./service/Geofences/GeofenceSlice";
import { VehicleApi } from "./service/Vehicles/Vehicles";
import { vehiclesSlice } from "./service/Vehicles/VehiclesSlice";
import { DriverApi } from "./service/Drivers/Drivers";
import { driversSlice } from "./service/Drivers/DriversSlice";
import { fsmDashboardApi } from "./service/fsm/FSMDashboard";
import userReducer from "./service/Users/UsersSlice";
import { SessionApi } from "./service/Session/Session";
import { CustomDevicesApi } from "./service/CustomDevices";
import { CustomUsersApi } from "./service/Users/CustomUsers";
import { CustomVehicleApi } from "./service/Vehicles/CustomVehicles";
import { AlertsApi } from "./service/Alerts/Alerts";
import { PermissionsApi } from "./service/Permissions/Permissions";
import { FormsPermissionsApi } from "./service/Permissions/FormsPermissions";
import { DevicestwoApi } from "./Devicestwo";
import { DriverTraccarApi } from "./service/DriversTraccar/DriversTraccar";
import { ExportsApi } from "./service/ExportReports/ExportReports";
import { NotificationsApi } from "./service/Notifications/Notifications";
import { InventorySetupApi } from "./service/InventorySetup/InventorySetup";
import { EntitieSetupApi } from "./service/Entities/Entities";
import { EventsReportApi } from "./service/ReportEvents/ReportEvents";
import { DashboardApi } from "./service/Dashboard";
import { dashboardSlice } from "./service/Dashboard/DashboardSlice";
import { ReportsApi } from "./service/NewReports/Reports";
import { ExportReportsApi } from "./service/NewReports/ExportReport";
import { GroupsApi } from "./service/Groups/Groups";
import { SummaryApi } from "./service/Summary/Summary";
import { GroupsBackendApi } from "./service/Groups/GroupsBack";
import { GroupsLinkApi } from "./service/Groups/GroupsLinkApi";
import { NewNotificationsApi } from "./service/Notifications/NewNotifications";
import { TechnicianApi } from "./service/Technician/Technician";
import { ShiftPoisApi } from "./service/Technician/ShiftPois";
import { MaintenanceApi } from "./service/Maintenance/Maintenance";
import tasksReducer from "./service/Tasks/tasksSlice";
import { FormsApi } from "./service/Forms/Form";
import { FormEntitiesApi } from "./service/Forms/FormEntities";
import { TasksApi } from "./service/Tasks/Tasks";
import { POISApi } from "./service/POIS/POIs";
import { RequestsApi } from "./service/Requests/Request";
import { CompaniesApi } from "./service/Companies/Company";
import { TeamsApi } from "./service/Teams/Teams";
import { WorkforceReportsApi } from "./service/NewReports/Workforce";
import { ServiceRequestReportApi } from "./service/NewReports/ServicesRequestReports";
import { POISHeatmapApi } from "./service/POIS/POIsHeatmap";
import { CustomersApi } from "./service/Customer/Customer";
import { FieldStaffPermissionsApi } from "./service/Permissions/FieldStaffPermissions";
import { CustomerPermissionsApi } from "./service/Permissions/CustomerPermissions";
import { POIsPermissionsApi } from "./service/Permissions/POIsPermissions";
import { POIAssignmentsApi } from "./service/Permissions/POIAssignments";
import { RolesPermissionsApi } from "./service/Roles&Permissions/RolesPermissions";
import { IRHistoryApi } from "./service/IRHistory/IRHistory";
import { consultantDashboardApi } from "./service/Consultant/ConsultantDashboard";
import { IRAPI } from "./service/IR/IR";
import { SnagApi } from "./service/Snag/Snag";
import { SubmittalsAPI } from "./service/Submittals/Submittals";

// ✅ Import IR WebSocket Reducer (default export)
import irWebSocketReducer from "./service/IR/IRWebSocket";
import snagWebSocketReducer from "./service/Snag/SnagWebSocket"; // Add this import
import { ProjectSetupAPI } from "./service/ProjectSetup/ProjectSetup";
import { ModDataApi } from "./service/mod-streaming/ModData";
import { AuthUsersApi } from "./service/Users/AuthUsers";
import { WebAuthnApi } from "./service/WebAuthn/WebAuthnApi";
import { ConfigurationsApi } from "./service/Configuration/Configuration";
import { MaintenanceNewApi } from "./service/Maintenance/MaintenanceNew";
import { ShelfAvailabilityApi } from "./service/NewReports/ShelfAvailability";
import { CallCenterApi } from "./service/CallCenter/CallCenter";
import { DispatcherApi } from "./service/Dispatcher/Dispatcher";
import { DispatcherPlanApi } from "./service/Dispatcher/DispatcherPlan";
import { LocationRequestsApi } from "./service/LocationRequests/LocationRequests";
import { SkuApi } from "./service/Sku/Sku";
import { AnalyticsApi } from "./service/Analytics/Analytics";
import { ProductExpirationApi } from "./service/NewReports/ProductExpiration";
import { ShelfMovementApi } from "./service/NewReports/ShelfMovement";

// --- Persist Config ---
const userFilter = createFilter("users", [
  "user",
  "auth",
  "offlineMode",
  "configId",
]);
const persistConfig = {
  key: "root",
  storage,
  whitelist: ["users"],
  transforms: [userFilter],
};

// --- Root Reducer ---
const rootReducer = combineReducers({
  [PositionsApi.reducerPath]: PositionsApi.reducer,
  [DevicesApi.reducerPath]: DevicesApi.reducer,
  [DevicestwoApi.reducerPath]: DevicestwoApi.reducer,
  [CustomDevicesApi.reducerPath]: CustomDevicesApi.reducer,
  [UsersApi.reducerPath]: UsersApi.reducer,
  [CustomUsersApi.reducerPath]: CustomUsersApi.reducer,
  users: userReducer,
  [GeofencesApi.reducerPath]: GeofencesApi.reducer,
  geofences: geofencesSlice.reducer,
  [VehicleApi.reducerPath]: VehicleApi.reducer,
  [CustomVehicleApi.reducerPath]: CustomVehicleApi.reducer,
  vehicles: vehiclesSlice.reducer,
  [DriverApi.reducerPath]: DriverApi.reducer,
  [DriverTraccarApi.reducerPath]: DriverTraccarApi.reducer,
  drivers: driversSlice.reducer,
  webSocket: webSocketSlice.reducer,
  irWebSocket: irWebSocketReducer, // ✅ Use the default export
  snagWebSocket: snagWebSocketReducer, // Add this line
  [SessionApi.reducerPath]: SessionApi.reducer,
  [AlertsApi.reducerPath]: AlertsApi.reducer,
  [ReportsApi.reducerPath]: ReportsApi.reducer,
  [EventsReportApi.reducerPath]: EventsReportApi.reducer,
  [ExportsApi.reducerPath]: ExportsApi.reducer,
  [NotificationsApi.reducerPath]: NotificationsApi.reducer,
  [NewNotificationsApi.reducerPath]: NewNotificationsApi.reducer,
  [InventorySetupApi.reducerPath]: InventorySetupApi.reducer,
  [EntitieSetupApi.reducerPath]: EntitieSetupApi.reducer,
  [DashboardApi.reducerPath]: DashboardApi.reducer,
  dashboards: dashboardSlice.reducer,
  [ExportReportsApi.reducerPath]: ExportReportsApi.reducer,
  [GroupsApi.reducerPath]: GroupsApi.reducer,
  [SummaryApi.reducerPath]: SummaryApi.reducer,
  [GroupsBackendApi.reducerPath]: GroupsBackendApi.reducer,
  [GroupsLinkApi.reducerPath]: GroupsLinkApi.reducer,
  [TechnicianApi.reducerPath]: TechnicianApi.reducer,
  [ShiftPoisApi.reducerPath]: ShiftPoisApi.reducer,
  [MaintenanceApi.reducerPath]: MaintenanceApi.reducer,
  tasks: tasksReducer,
  [FormsApi.reducerPath]: FormsApi.reducer,
  [FormEntitiesApi.reducerPath]: FormEntitiesApi.reducer,
  [TasksApi.reducerPath]: TasksApi.reducer,
  [POISApi.reducerPath]: POISApi.reducer,
  [RequestsApi.reducerPath]: RequestsApi.reducer,
  [WorkforceReportsApi.reducerPath]: WorkforceReportsApi.reducer,
  [ServiceRequestReportApi.reducerPath]: ServiceRequestReportApi.reducer,
  [POISHeatmapApi.reducerPath]: POISHeatmapApi.reducer,
  [fsmDashboardApi.reducerPath]: fsmDashboardApi.reducer,
  [PermissionsApi.reducerPath]: PermissionsApi.reducer,
  [FormsPermissionsApi.reducerPath]: FormsPermissionsApi.reducer,
  [CustomersApi.reducerPath]: CustomersApi.reducer,
  [FieldStaffPermissionsApi.reducerPath]: FieldStaffPermissionsApi.reducer,
  [CustomerPermissionsApi.reducerPath]: CustomerPermissionsApi.reducer,
  [POIsPermissionsApi.reducerPath]: POIsPermissionsApi.reducer,
  [POIAssignmentsApi.reducerPath]: POIAssignmentsApi.reducer,
  [RolesPermissionsApi.reducerPath]: RolesPermissionsApi.reducer,
  [IRHistoryApi.reducerPath]: IRHistoryApi.reducer,
  [consultantDashboardApi.reducerPath]: consultantDashboardApi.reducer,
  [IRAPI.reducerPath]: IRAPI.reducer,
  [SnagApi.reducerPath]: SnagApi.reducer,
  [SubmittalsAPI.reducerPath]: SubmittalsAPI.reducer,
  [ProjectSetupAPI.reducerPath]: ProjectSetupAPI.reducer,
  [CompaniesApi.reducerPath]: CompaniesApi.reducer,
  [TeamsApi.reducerPath]: TeamsApi.reducer,
  [ModDataApi.reducerPath]: ModDataApi.reducer,
  [AuthUsersApi.reducerPath]: AuthUsersApi.reducer,
  [WebAuthnApi.reducerPath]: WebAuthnApi.reducer,
  [ConfigurationsApi.reducerPath]: ConfigurationsApi.reducer,
  [MaintenanceNewApi.reducerPath]: MaintenanceNewApi.reducer,
  [ShelfAvailabilityApi.reducerPath]: ShelfAvailabilityApi.reducer,
  [ProductExpirationApi.reducerPath]: ProductExpirationApi.reducer,
  [ShelfMovementApi.reducerPath]: ShelfMovementApi.reducer,
  [SkuApi.reducerPath]: SkuApi.reducer,
  [CallCenterApi.reducerPath]: CallCenterApi.reducer,
  [DispatcherApi.reducerPath]: DispatcherApi.reducer,
  [DispatcherPlanApi.reducerPath]: DispatcherPlanApi.reducer,
  [LocationRequestsApi.reducerPath]: LocationRequestsApi.reducer,
  [AnalyticsApi.reducerPath]: AnalyticsApi.reducer,
});

// ... rest of your store code stays the same
const persistedReducer = persistReducer(persistConfig, rootReducer);

const allMiddleware = [
  PositionsApi.middleware,
  DevicesApi.middleware,
  DevicestwoApi.middleware,
  DashboardApi.middleware,
  CustomDevicesApi.middleware,
  DriverTraccarApi.middleware,
  UsersApi.middleware,
  CustomUsersApi.middleware,
  GeofencesApi.middleware,
  VehicleApi.middleware,
  CustomVehicleApi.middleware,
  DriverApi.middleware,
  SessionApi.middleware,
  AlertsApi.middleware,
  ReportsApi.middleware,
  EventsReportApi.middleware,
  PermissionsApi.middleware,
  FormsPermissionsApi.middleware,
  ExportsApi.middleware,
  NotificationsApi.middleware,
  InventorySetupApi.middleware,
  EntitieSetupApi.middleware,
  ExportReportsApi.middleware,
  GroupsApi.middleware,
  SummaryApi.middleware,
  GroupsBackendApi.middleware,
  GroupsLinkApi.middleware,
  NewNotificationsApi.middleware,
  TechnicianApi.middleware,
  ShiftPoisApi.middleware,
  MaintenanceApi.middleware,
  FormEntitiesApi.middleware,
  FormsApi.middleware,
  TasksApi.middleware,
  POISApi.middleware,
  RequestsApi.middleware,
  CompaniesApi.middleware,
  WorkforceReportsApi.middleware,
  ServiceRequestReportApi.middleware,
  POISHeatmapApi.middleware,
  fsmDashboardApi.middleware,
  CustomersApi.middleware,
  FieldStaffPermissionsApi.middleware,
  CustomerPermissionsApi.middleware,
  POIsPermissionsApi.middleware,
  POIAssignmentsApi.middleware,
  RolesPermissionsApi.middleware,
  IRHistoryApi.middleware,
  consultantDashboardApi.middleware,
  IRAPI.middleware,
  SnagApi.middleware,
  SubmittalsAPI.middleware,
  ProjectSetupAPI.middleware,
  TeamsApi.middleware,
  ModDataApi.middleware,
  AuthUsersApi.middleware,
  WebAuthnApi.middleware,
  ConfigurationsApi.middleware,
  MaintenanceNewApi.middleware,
  ShelfAvailabilityApi.middleware,
  ProductExpirationApi.middleware,
  ShelfMovementApi.middleware,
  SkuApi.middleware,
  CallCenterApi.middleware,
  DispatcherApi.middleware,
  DispatcherPlanApi.middleware,
  LocationRequestsApi.middleware,
  AnalyticsApi.middleware,
];

const uniqueMiddleware = Array.from(new Set(allMiddleware));

const duplicates = allMiddleware.filter(
  (item, idx) => allMiddleware.indexOf(item) !== idx,
);
if (duplicates.length) {
  console.warn("⚠️ Duplicate middleware removed:", duplicates);
}

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(uniqueMiddleware),
});

export const persistor = persistStore(store);

setupListeners(store.dispatch);
